import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {TagUpdateOptions} from 'file-browser/FinderActions';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import LoadingFullScreen from 'nav/full-screen/LoadingFullScreen';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';

import {
  LinkedContainerContentsQuery,
  LinkedContainerContentsQueryVariables,
} from './__generated/LinkedContainerContentsQuery';
import {
  TagSchemaInterfacesQuery,
  TagSchemaInterfacesQueryVariables,
} from './__generated/TagSchemaInterfacesQuery';
import {
  EXPIRATION_DATE_FIELD_DISPLAY_NAME,
  EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID,
} from './ValidContentsConstants';

// Queries visible for tests
export const TAG_SCHEMA_INTERFACES_QUERY: DocumentNode = gql`
  query TagSchemaInterfacesQuery($id: StrictString!) @skipAudit @engComponent(name: SCHEMAS) {
    tagSchema(id: $id) {
      ...TagSchemaInterfacesGraphQL
      ... on InaccessibleInfoNode {
        invalidId
      }
    }
  }
  ${getFragmentWrapper('TagSchemaInterfaces').fragment}
`;

// Accessible, not archived containers will have container.name populated.
// Archived containers will appear as `"container": null`.
export const LINKED_CONTAINER_CONTENTS_QUERY: DocumentNode = gql`
  query LinkedContainerContentsQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    bioentities(ids: $ids) {
      ... on RegistryItemInterface {
        id
        tagSchema {
          id
          registry {
            id
          }
        }
        linkedContainerContents {
          container(includeArchived: false) {
            ... on ContainerNode {
              id
              name
              storableSchema {
                id
                registry {
                  id
                }
              }
              expirationInfo {
                id
                isOverridden
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type BioentityGQL = ExcludeInaccessible<LinkedContainerContentsQuery['bioentities'][number]>;
type LinkedContainerOrInaccessible = NonNullable<
  ExcludeInaccessible<BioentityGQL>['linkedContainerContents'][number]['container']
>;
type LinkedContainer = ExcludeInaccessible<LinkedContainerOrInaccessible>;

function tagIsUpdated(tagUpdateOptions: TagUpdateOptions, tagDisplayName: string): boolean {
  return (
    tagUpdateOptions.toSet[tagDisplayName] !== undefined ||
    tagUpdateOptions.toAdd?.includes(tagDisplayName) ||
    tagUpdateOptions.toRemove.includes(tagDisplayName) ||
    tagUpdateOptions.toRename[tagDisplayName] !== undefined
  );
}

async function hasExpirationTrackingSchemaInterface(id: StrictString): Promise<Boolean> {
  // If the tag schema has interfaces but config disables interfaces, fetch will fail.
  const result = await doQuerySafe<TagSchemaInterfacesQuery, TagSchemaInterfacesQueryVariables>(
    {query: TAG_SCHEMA_INTERFACES_QUERY},
    {variables: {id}, fetchPolicy: 'cache-first'}
  );
  if (result && result.tagSchema.__typename === 'TagSchemaNode') {
    for (const schemaInterface of result.tagSchema.interfaces ?? []) {
      if (schemaInterface.id === EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID) {
        return true;
      }
    }
  }
  return false;
}

export interface ConfirmExpirationDateResult {
  shouldProceed: boolean;
}

export interface ConfirmEntitiesExpirationDateModalProps {
  onRequestSubmit: (result: ConfirmExpirationDateResult) => void;
  onRequestClose: () => void;
  bioentityIds: Array<string>;
}

function getAffectedContainerCounts(bioentities: Array<BioentityGQL>): {
  accessible: number;
  inaccessible: number;
} {
  return bioentities.reduce(
    (counts: {accessible: number; inaccessible: number}, bioentity) => {
      const unarchivedContainers = bioentity.linkedContainerContents
        .map((content) => content.container)
        .filter((container): container is LinkedContainerOrInaccessible => container != null);
      const accessibleContainers = unarchivedContainers.filter(
        (container): container is LinkedContainer => !isInaccessible(container)
      );
      const correctRegistryContainers = accessibleContainers.filter(
        (container) => container.storableSchema.registry.id === bioentity.tagSchema?.registry?.id
      );
      const inaccessibleCount = unarchivedContainers.length - accessibleContainers.length;
      const accessibleInheritingContainers = correctRegistryContainers.filter(
        (container) => !container.expirationInfo.isOverridden
      ).length;
      return {
        accessible: counts.accessible + accessibleInheritingContainers,
        inaccessible: counts.inaccessible + inaccessibleCount,
      };
    },
    {accessible: 0, inaccessible: 0}
  );
}

/**
 * Hook to check entities for downstream inheriting containers, and render a warning banner if present.
 *
 * @param bioentityIds entity ids to check for inheriting containers
 * @param renderFullScreenLoading controls whether to render a full loading screen or just an inline throbber.
 * Former is needed when opening up the modal.
 * @param bannerClassName class name to style banner, if shown
 *
 * @return Object {resultType, banner} Query result for handling pending/error cases, and optional banner to render
 */
function useEntitiesInheritingContainers(
  bioentityIds: Array<string>,
  renderFullScreenLoading: boolean = true,
  bannerClassName?: string
): {
  resultType: QueryResultType;
  banner: JSX.Element | null;
} {
  const result = useBenchlingQuery<LinkedContainerContentsQuery, LinkedContainerContentsQueryVariables>(
    LINKED_CONTAINER_CONTENTS_QUERY,
    {
      variables: {ids: bioentityIds},
      fetchPolicy: 'cache-and-network',
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return {
      resultType: result.type,
      banner: renderFullScreenLoading ? <LoadingFullScreen /> : <CenteredHorizontalThrobber />,
    };
  } else if (result.type === QueryResultType.ERROR) {
    return {resultType: result.type, banner: null};
  }

  const bioentities = accessibleObjs(result.data.bioentities);
  const affectedContainerCounts = getAffectedContainerCounts(bioentities);
  const maxAffectedContainerCount = affectedContainerCounts.accessible + affectedContainerCounts.inaccessible;

  if (maxAffectedContainerCount === 0) {
    return {resultType: QueryResultType.SUCCESS, banner: null};
  }

  const entityStr = pluralize(bioentities.length, 'this entity', 'these entities');
  const countPrefix = affectedContainerCounts.inaccessible > 0 ? 'Up to ' : '';
  const containerWellStr =
    maxAffectedContainerCount === 1
      ? `${countPrefix}1 container or well inherits the expiration date of ${entityStr}.`
      : `${countPrefix}${maxAffectedContainerCount} containers or wells inherit the expiration date of ${entityStr}.`;
  const inheritingMessage = `also apply to ${pluralize(
    bioentities.length,
    'its',
    'their'
  )} inheriting containers`;

  return {
    resultType: QueryResultType.SUCCESS,
    banner: (
      <div className={bannerClassName}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`${containerWellStr} Changes to the expiration date for ${entityStr} will ${inheritingMessage}.`}
        />
      </div>
    ),
  };
}

/**
 * Modal to render info about entity expiration changes affecting inheriting containers.
 *
 * Handles querying for any inheriting containers & choosing whether to render the modal.
 */
export function ConfirmEntitiesExpirationDateModal(
  props: ConfirmEntitiesExpirationDateModalProps
): JSX.Element | null {
  const {resultType, banner} = useEntitiesInheritingContainers(props.bioentityIds);

  if (resultType !== QueryResultType.SUCCESS) {
    return banner;
  }
  // Query succeeded, but no banner means we can proceed without confirming (no inheriting containers found)
  if (banner == null) {
    props.onRequestSubmit({shouldProceed: true});
    return null;
  }
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Cancel',
          submit: false,
          onClick: () => {
            props.onRequestSubmit({shouldProceed: false});
          },
        },
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Continue',
        },
      ]}
      onRequestClose={() => {
        props.onRequestSubmit({shouldProceed: false});
      }}
      onRequestSubmit={() => {
        props.onRequestSubmit({shouldProceed: true});
      }}
      title="Edit expiration date"
    >
      {banner}
    </Modal>
  );
}

/**
 * Lightweight wrapper to confirm expiration date edit specifically for a single entity field update.
 *
 * Calls `confirmExpirationDateEdits` which handles running the confirmation modal if necessary.
 *
 * @param tagSchemaId tag schema id of the entity
 * @param tagUpdateOptions schema tag update object
 * @param bioentityId entity being updated
 *
 * @return promise for whether caller can proceed with update (continued past confirmation).
 */
export async function confirmExpirationDateIfEdited(
  tagSchemaId: string,
  tagUpdateOptions: TagUpdateOptions,
  bioentityId: string
): Promise<ConfirmExpirationDateResult> {
  const bioentityHasExpirationUpdate = (): boolean =>
    tagIsUpdated(tagUpdateOptions, EXPIRATION_DATE_FIELD_DISPLAY_NAME);
  return await confirmExpirationDateEdits(tagSchemaId, [bioentityId], bioentityHasExpirationUpdate);
}

/**
 * Confirm expiration date edits for multiple entities.
 *
 * Runs modal if expiration tracking is enabled & inheriting containers are found.
 *
 * Supports either just passing in entities already filtered to those with edits, or a function to check which
 * entities have updates.
 *
 * @param tagSchemaId tag schema id of the entity
 * @param bioentityIds entity ids to check for downstream containers
 * @param bioentityHasExpirationUpdate function to filter entity ids to check based on whether they have
 * expiration updates. By default, includes all provided ids.
 *
 * @return promise for whether caller can proceed with update (continued past confirmation).
 */
export async function confirmExpirationDateEdits(
  tagSchemaId: string,
  bioentityIds: Array<string>,
  bioentityHasExpirationUpdate: (id: string) => boolean = () => true
): Promise<ConfirmExpirationDateResult> {
  if (!BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE) {
    return {shouldProceed: true};
  }
  const bioentityIdsWithUpdates = bioentityIds.filter((id) => bioentityHasExpirationUpdate(id));
  const checkContainers =
    bioentityIdsWithUpdates.length > 0 ? await hasExpirationTrackingSchemaInterface(tagSchemaId) : false;
  if (!checkContainers) {
    return {shouldProceed: true};
  }
  const result = await runModal<ConfirmEntitiesExpirationDateModalProps>(ConfirmEntitiesExpirationDateModal, {
    bioentityIds: bioentityIdsWithUpdates,
  });
  return result ?? {shouldProceed: false};
}

/**
 * Lightweight wrapper around the hook to query for inheriting containers and return just the optional banner.
 *
 * Throbber is rendered while we're checking for inheriting containers.
 *
 * Pulled into a separate component to satisfy the rules around where hooks can be called (functional
 * components, not after an early return).
 */
function ConfirmEntitiesExpirationDateBanner(props: {
  bioentityIds: Array<string>;
  bannerClassName?: string;
}): JSX.Element | null {
  const {banner} = useEntitiesInheritingContainers(props.bioentityIds, false, props.bannerClassName);

  return banner;
}

/**
 * Checks for inheriting containers & returns only the optional confirmation banner.
 *
 * Does not return any loading status, or wait on user confirmation. For use in existing modals where we
 * just want to display the information.
 *
 * @param tagSchemaId tag schema id of the entity
 * @param bioentityIds entity ids to check for downstream containers
 * @param tagName Name of the tag being updated
 *
 * @return banner to render
 */
export async function renderPossibleExpirationDateConfirmation(
  tagSchemaId: string,
  bioentityIds: Array<string>,
  tagName: string
): Promise<JSX.Element | null> {
  if (!BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE || tagName !== EXPIRATION_DATE_FIELD_DISPLAY_NAME) {
    return null;
  }
  const checkContainers =
    bioentityIds.length > 0 ? await hasExpirationTrackingSchemaInterface(tagSchemaId) : false;
  if (!checkContainers) {
    return null;
  }
  return (
    <ConfirmEntitiesExpirationDateBanner bioentityIds={bioentityIds} bannerClassName="u-input-spacing-top" />
  );
}
